import { z } from "zod";
import { lazySchema } from "@repo/lazy-schema";

export const IdleAssetReconciliationCreateSchema = lazySchema(() =>
  z
    .object({
      requestKey: z.string().min(1).max(256),
      portfolioId: z.string().min(1).max(200),
      intent: z.object({ kind: z.literal("reconcile_idle_assets") }).strict(),
    })
    .strict(),
);
export type IdleAssetReconciliationCreate = z.infer<
  typeof IdleAssetReconciliationCreateSchema
>;
export const IdleAssetReconciliationNoOpSchema = lazySchema(() =>
  z
    .object({
      outcome: z.literal("no_op"),
      portfolioId: z.string(),
      reason: z.enum([
        "below_minimum",
        "no_available_balance",
        "route_unavailable",
      ]),
    })
    .strict(),
);
export type IdleAssetReconciliationNoOp = z.infer<
  typeof IdleAssetReconciliationNoOpSchema
>;

export class IdleReconciliationOccurrenceError extends Error {
  constructor() {
    super("idle_occurrence_expired_or_invalid");
    this.name = "IdleReconciliationOccurrenceError";
  }
}

/** The occurrence time is part of the key, never a financial input. Replays of
 * accepted operations remain readable after expiry; new admissions do not.
 */
export function idleReconciliationRequestKey(
  portfolioId: string,
  scheduledAt: number,
): string {
  if (!Number.isSafeInteger(scheduledAt) || scheduledAt < 0)
    throw new Error("idle_occurrence_invalid");
  return `idle-assets:v1:${scheduledAt}:${portfolioId}`;
}
export function assertIdleReconciliationOccurrence(
  input: IdleAssetReconciliationCreate,
  now: number,
): void {
  const match = /^idle-assets:v1:(\d+):/.exec(input.requestKey);
  const scheduledAt = match ? Number(match[1]) : NaN;
  if (
    !Number.isSafeInteger(scheduledAt) ||
    input.requestKey !==
      idleReconciliationRequestKey(input.portfolioId, scheduledAt) ||
    scheduledAt > now ||
    now >= scheduledAt + 86_400_000
  )
    throw new IdleReconciliationOccurrenceError();
}

/** Operator-triggered replay/new occurrence of a configured nightly scan.
 * No financial inputs or cooldown overrides are accepted. Keep scheduledAt
 * unchanged on retries; accepted executions dedupe with the cron's own keys.
 */
export const IdleAssetScanRequestSchema = lazySchema(() =>
  z
    .object({
      scheduleId: z.string().min(1).max(100),
      scheduledAt: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    })
    .strict(),
);
export type IdleAssetScanRequest = z.infer<typeof IdleAssetScanRequestSchema>;
